/**
 * Message sender identity — "who is speaking" for EVERY message.
 *
 * Owner directive (2026-07-24, all-accounts rollout): a thread is no longer a
 * back-and-forth with one assistant, so every message must say who sent it as
 * plainly as Slack does — Prest0n included — and there is no such thing as a
 * "group" vs "non-group" thread in the UI. So this resolver is NOT gated on any
 * group flag and NOT limited to other people's turns: it answers for the
 * viewer's own messages, other participants' messages, and Prest0n's.
 *
 * Rendering contract it feeds (see MessageSenderChip + the meta row):
 *   • `side`: the viewer's own messages render RIGHT, everyone else's — humans
 *     AND Prest0n — render LEFT. Business threads have several humans talking,
 *     so "right" must mean "me", never "a human".
 *   • `label`: what the bottom-of-message meta row shows.
 *   • `card`: the pop-out identity card (same interaction as the usage ⓘ).
 *
 * Pure (no React / Firebase / clock) so it unit-tests in isolation.
 */
import { ASSISTANT_SENDER_ID } from '../../types/groupThreadMessageDoc';
// T1 (2026-09-10): a post Prest0n composed at someone's instruction wears
// THEIR name and avatar — and carries a permanent mark saying so (T-R8).
import { delegationOf, type MessageDelegation } from '../../types/messageAuthorship';
import { senderGlyph } from './senderGlyph';

/** Assistant display name — the one place the product name is asserted. */
export const ASSISTANT_DISPLAY_NAME = 'Prest0n';

/**
 * Names a HUMAN participant may not present under.
 *
 * INCIDENT 2026-08-05: a real member account (`prest0n@prest0.ai`) carried the
 * display name "Prest0n", so its human turns rendered as a gold "P" chip
 * labelled Prest0n while genuine assistant turns rendered as the ink-dark ★ —
 * the same name under two identities in one thread. The account is not the only
 * route in: member `displayName` is seeded from `profile.displayName ||
 * profile.firmName`, and `firmName` defaults to the literal product name in
 * several provisioning paths, so any account could acquire a member labelled
 * "Prest0n" without anyone typing it.
 *
 * Reserving the name here is deliberate: it is the single render path every
 * message passes through, so the guarantee "exactly one Prest0n in any thread"
 * holds no matter what a roster, a profile or a future provisioning path says.
 * Mirrors the server's RESERVED_THREAD_TITLES precedent.
 */
const RESERVED_ASSISTANT_NAMES: ReadonlySet<string> = new Set([
  ASSISTANT_DISPLAY_NAME.toLowerCase(),
  'preston',
  'prest0n ai',
  'preston ai',
]);

export function isReservedAssistantName(label: string | null | undefined): boolean {
  const trimmed = (label ?? '').trim().toLowerCase();
  if (!trimmed) return false;
  return RESERVED_ASSISTANT_NAMES.has(trimmed);
}

/**
 * A human label that never collides with the assistant.
 *
 * Falls back to the email local-part, then the short uid tag — both already
 * exist elsewhere in this module, so a colliding member still reads as a
 * recognisable person rather than an invented "Prest0n (2)".
 */
export function disambiguateParticipantLabel(
  label: string,
  profile: { email?: string } | undefined,
  senderId: string,
): string {
  if (!isReservedAssistantName(label)) return label;
  const email = (profile?.email ?? '').trim();
  const local = email.includes('@') ? email.slice(0, email.indexOf('@')).trim() : email;
  if (local && !isReservedAssistantName(local)) return local;
  // No fallback to the FULL email: a raw address makes a poor chip label, and
  // the identity card already carries it. The production account is exactly this
  // case — prest0n@prest0.ai, whose local-part collides too — so it lands here.
  return shortUidTag(senderId);
}

/** Roster facts for the pop-out card, keyed by uid. */
export interface SenderProfile {
  label?: string;
  email?: string;
  role?: string;
  isOwner?: boolean;
}

export type SenderKind = 'self' | 'participant' | 'assistant';

/**
 * The permanent mark on a delegated post (T-R8, 2026-09-10).
 *
 * Four properties, all load-bearing and all satisfied here rather than by a
 * caller's discretion: PERMANENT (a property of the message, so it survives
 * reload, export and every later reader), SUBTLE BUT PRESENT (a small glyph at
 * rest, the detail on hover), VISIBLE TO EVERYONE (including the person who
 * initiated it — nobody gets a view where their own delegated message looks
 * hand-typed), and legible to PREST0N himself when he reads the thread back
 * (that half is the transcript bracket, server-side in threadLedger).
 *
 * The wording is deterministic on purpose (D9's safety half): a provenance
 * mark the model could phrase away is not a provenance mark.
 */
export interface SenderDelegationMark {
  /** The small glyph shown after the name. Prest0n's own star: his hand in it. */
  glyph: string;
  /** One plain sentence, revealed on hover and read out to assistive tech. */
  detail: string;
}

export interface MessageSenderIdentity {
  kind: SenderKind;
  /** Bottom-row label, e.g. 'You', 'Malaika QA', 'Prest0n'. Never blank. */
  label: string;
  /** Which side of the transcript this message renders on. */
  side: 'right' | 'left';
  /** Avatar glyph (first visible char of the underlying name), or '★' for Prest0n. */
  initial: string;
  /**
   * Present ONLY on a post Prest0n composed in this person's name. Absent on
   * every ordinary message, so nothing about existing rendering changes.
   */
  delegation?: SenderDelegationMark;
  /** Pop-out card contents. `subtitle` may be blank when nothing resolved. */
  card: {
    title: string;
    subtitle: string;
    /** Extra lines, already ordered for display (email, role, "You", …). */
    rows: Array<{ label: string; value: string }>;
  };
}

export interface ResolveMessageSenderArgs {
  message: {
    isUser?: boolean;
    senderId?: string | null;
    senderDisplayName?: string | null;
    /** T1: who COMPOSED the words (src/types/messageAuthorship). */
    authorship?: unknown;
  };
  /** Signed-in uid; own messages resolve to kind 'self'. */
  currentUid?: string | null;
  /** uid → roster facts (from the account members / owner profile / self). */
  profiles?: Record<string, SenderProfile | undefined>;
  /** Viewer's own display name, for the card title on their own messages. */
  selfLabel?: string | null;
}

/**
 * First visible character, uppercased; '?' when nothing usable.
 * Re-exported from the import-free leaf so a caller that wants only the
 * initial does not drag this module's Firestore graph in with it. Imported
 * rather than `export … from`, because this module calls it too and a bare
 * re-export binds no local name.
 */
export { senderGlyph };

/**
 * Resolve the sender identity for one message.
 *
 * Attribution rules, in order:
 *   1. a non-user (assistant) turn → Prest0n, left;
 *   2. a human turn whose senderId is the viewer, OR that carries NO senderId at
 *      all (solo threads and every legacy/local bubble — the viewer is the only
 *      possible author of their own tree) → self, right;
 *   3. any other human senderId → that participant, left.
 * Rule 2's no-senderId case is what keeps single-participant threads and old
 * transcripts rendering exactly as before, with "You" now labelled explicitly.
 */
/** Prest0n's own glyph, reused as the delegation mark: his hand in this message. */
const DELEGATION_GLYPH = '\u2605';

/**
 * The one sentence a reader discovers on hover. Second person to the person who
 * asked for it, third person to everyone else — the same courtesy the refusal
 * copy keeps (rbac/permissionRefusalCopy). It says three things and nothing
 * else: Prest0n posted it, it wears this name, and it was at their instruction.
 */
export function delegationDetailCopy(
  delegation: MessageDelegation,
  who: { kind: SenderKind; label: string },
): string {
  const surface = delegation.via === 'voice'
    ? (who.kind === 'self' ? ' from your voice session' : ' from their voice session')
    : '';
  const whose = who.kind === 'self' ? 'your name' : `${who.label}'s name`;
  const asked = who.kind === 'self' ? 'your instruction' : 'their instruction';
  return `Posted by ${ASSISTANT_DISPLAY_NAME} in ${whose}${surface} — his words, at ${asked}.`;
}

/** The card row that says the same thing where the pop-out card can hold it. */
function delegationCardRow(delegation: MessageDelegation): { label: string; value: string } {
  return {
    label: 'Posted by',
    value: delegation.via === 'voice'
      ? `${ASSISTANT_DISPLAY_NAME} (voice)`
      : ASSISTANT_DISPLAY_NAME,
  };
}

export function resolveMessageSender(args: ResolveMessageSenderArgs): MessageSenderIdentity {
  const senderId = (args.message?.senderId ?? '').trim();
  const uid = (args.currentUid ?? '').trim();
  const profiles = args.profiles ?? {};

  const isAssistantTurn = args.message?.isUser !== true || senderId === ASSISTANT_SENDER_ID;
  if (isAssistantTurn) {
    return {
      kind: 'assistant',
      label: ASSISTANT_DISPLAY_NAME,
      side: 'left',
      initial: '★',
      card: {
        title: ASSISTANT_DISPLAY_NAME,
        subtitle: 'AI teammate',
        rows: [{ label: 'Role', value: 'Assistant' }],
      },
    };
  }

  const isSelf = !senderId || senderId === uid;
  if (isSelf) {
    const own = profiles[uid] ?? {};
    // Same reservation on the viewer's own card title: signing in AS the
    // colliding account must not put a second "Prest0n" on screen either.
    const name = disambiguateParticipantLabel(
      (args.selfLabel ?? '').trim() || (own.label ?? '').trim(),
      own,
      uid,
    );
    const rows: Array<{ label: string; value: string }> = [];
    if (own.email) rows.push({ label: 'Email', value: own.email });
    if (own.role) rows.push({ label: 'Role', value: own.role });
    // T1: a post Prest0n composed in the viewer's own name still reads as
    // theirs — with the mark, for them most of all (T-R8: nobody, the author
    // least of all, gets a view where their delegated message looks typed).
    const selfDelegation = delegationOf(args.message as Record<string, unknown>);
    if (selfDelegation) rows.push(delegationCardRow(selfDelegation));
    return {
      kind: 'self',
      label: 'You',
      side: 'right',
      initial: senderGlyph(name || 'You'),
      ...(selfDelegation
        ? {
          delegation: {
            glyph: DELEGATION_GLYPH,
            detail: delegationDetailCopy(selfDelegation, { kind: 'self', label: name || 'You' }),
          },
        }
        : {}),
      card: {
        title: name || 'You',
        subtitle: 'You',
        rows,
      },
    };
  }

  const profile = profiles[senderId] ?? {};
  // Server-stamped fallback (2026-08-26): the name resolved at WRITE time and
  // carried on the message doc. Sits below the viewer's roster label (which
  // may be fresher — a rename) and above the uid tag, which is exactly the
  // "Teammate kFZ5" the owner reported. The reservation guard still applies —
  // a stamped name may never read as Prest0n.
  const stampedName = (args.message.senderDisplayName ?? '').trim();
  // A human may not present as Prest0n — see RESERVED_ASSISTANT_NAMES.
  const label = disambiguateParticipantLabel(
    (profile.label ?? '').trim()
      || (!isReservedAssistantName(stampedName) ? stampedName : '')
      || shortUidTag(senderId),
    profile,
    senderId,
  );
  const rows: Array<{ label: string; value: string }> = [];
  if (profile.email) rows.push({ label: 'Email', value: profile.email });
  if (profile.role) rows.push({ label: 'Role', value: profile.role });
  if (profile.isOwner) rows.push({ label: 'Workspace', value: 'Owner' });
  const delegation = delegationOf(args.message as Record<string, unknown>);
  if (delegation) rows.push(delegationCardRow(delegation));
  return {
    kind: 'participant',
    label,
    side: 'left',
    initial: senderGlyph(label),
    ...(delegation
      ? { delegation: { glyph: DELEGATION_GLYPH, detail: delegationDetailCopy(delegation, { kind: 'participant', label }) } }
      : {}),
    card: {
      title: label,
      subtitle: profile.isOwner ? 'Workspace owner' : 'Teammate',
      rows,
    },
  };
}

/** Readable stand-in when the roster hasn't resolved a name yet. */
export function shortUidTag(uid: string): string {
  const trimmed = (uid ?? '').trim();
  if (!trimmed) return 'Teammate';
  return `Teammate ${trimmed.slice(0, 4)}`;
}

/** Assemble the uid → profile map for the cards from roster-shaped rows. */
export function buildSenderProfiles(sources: {
  members?: ReadonlyArray<{ userId?: string; displayName?: string; email?: string; role?: string; isOwner?: boolean }> | null;
  ownerUid?: string | null;
  labels?: Record<string, string>;
}): Record<string, SenderProfile> {
  const out: Record<string, SenderProfile> = {};
  const ownerUid = (sources.ownerUid ?? '').trim();
  for (const m of sources.members ?? []) {
    const uid = (m?.userId ?? '').trim();
    if (!uid) continue;
    const label = (m?.displayName ?? '').trim() || (m?.email ?? '').trim();
    out[uid] = {
      ...(label ? { label } : {}),
      ...(m?.email ? { email: String(m.email).trim() } : {}),
      ...(m?.role ? { role: String(m.role).trim() } : {}),
      ...(m?.isOwner === true || (ownerUid && uid === ownerUid) ? { isOwner: true } : {}),
    };
  }
  // Label map fills gaps (owner resolved from the profile, self from auth) so a
  // sender never degrades to a uid tag when SOME source knew their name.
  for (const [uid, label] of Object.entries(sources.labels ?? {})) {
    const key = (uid ?? '').trim();
    const value = (label ?? '').trim();
    if (!key || !value) continue;
    if (!out[key]) out[key] = { label: value };
    else if (!out[key].label) out[key] = { ...out[key], label: value };
    if (ownerUid && key === ownerUid) out[key] = { ...out[key], isOwner: true };
  }
  return out;
}

/**
 * ONE normalizer for participant counts (sidebar sections round 3,
 * 2026-08-01): both the header label and the group-thread predicate below go
 * through this, so the two can never disagree about what a count "is".
 */
function normalizeParticipantCount(count: number): number {
  return Number.isFinite(count) ? Math.max(0, Math.floor(count)) : 0;
}

/**
 * Participant-count copy for the thread header. The owner directive is that a
 * thread is never LABELLED as group/non-group — the count alone tells the user
 * how many people are in it, so 1 renders as "Just you".
 */
export function participantCountLabel(count: number): string {
  const n = normalizeParticipantCount(count);
  if (n <= 1) return 'Just you';
  return `${n} people`;
}

/**
 * The header's people line WITH the Prest0n fact (2026-09-08): a thread a
 * permitted user took Prest0n out of says so where the count is, because
 * "Prest0n isn't answering" must never be a mystery. Prest0n-in stays the
 * unlabelled default (every thread has it; the count alone tells the story).
 */
export function participantLineLabel(count: number, assistantEnabled: boolean = true): string {
  const base = participantCountLabel(count);
  return assistantEnabled ? base : `${base} · without Prest0n`;
}

/**
 * Group-thread predicate (sidebar sections round 3, 2026-08-01): a thread is a
 * GROUP thread exactly when the header would NOT say "Just you" — i.e. the
 * server-mediated membership registry reports ≥2 distinct participants. Shares
 * `normalizeParticipantCount` with `participantCountLabel`, so the sidebar's
 * "group threads" section and the header's participant line are derived from
 * the SAME source (GlobalChatSystem's `sharedThreadParticipants`, hydrated
 * from the fail-closed `threadMembershipRead` CF) and the SAME numeric rule —
 * they structurally cannot disagree. A thread absent from the registry
 * (callers pass `count ?? 1`, mirroring the header) is a solo thread.
 */
export function isGroupParticipantCount(count: number): boolean {
  return normalizeParticipantCount(count) >= 2;
}
